import { EMAIL_INBOUND, type InboundEmail } from "@brains/contracts";
import {
  ServicePlugin,
  type ServicePluginContext,
  type Tool,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import packageJson from "../package.json";
import { MailTriageInboxSource } from "./inbox-source";
import {
  createMailClassifier,
  DEFAULT_EMAIL_TRIAGE_CLASSIFICATION_PROMPT,
  EMAIL_TRIAGE_CLASSIFICATION_PROMPT_TARGET,
} from "./lib/classifier";
import { EntityMailItemRepository } from "./mail-item-repository";
import { MailTriageOperatorService } from "./operator-service";
import { createEmailTriageListTool } from "./operator-tool";
import {
  emailWorkflowsConfigSchema,
  type EmailWorkflowsConfig,
  type EmailWorkflowsConfigInput,
} from "./schemas/config";
import { EmailWorkflowsSourceReader } from "./source-read";
import {
  MailThreadOrdinalCoordinator,
  threadOrdinalStateSchema,
} from "./thread-ordinal-coordinator";
import { EmailTriageProcessor } from "./triage-processor";

export class EmailWorkflowsPlugin extends ServicePlugin<
  EmailWorkflowsConfig,
  EmailWorkflowsConfigInput
> {
  private operator: MailTriageOperatorService | undefined;
  private threadOrdinals: MailThreadOrdinalCoordinator | undefined;

  constructor() {
    super("email-workflows", packageJson, {}, emailWorkflowsConfigSchema);
  }

  protected override async onRegister(
    context: ServicePluginContext,
  ): Promise<void> {
    this.operator = new MailTriageOperatorService(context);
    this.threadOrdinals = new MailThreadOrdinalCoordinator({
      entityService: context.entityService,
      state: context.runtimeState.scoped({
        namespace: "email-workflows.thread-ordinals",
        schema: threadOrdinalStateSchema,
      }),
    });
    const sourceReader = new EmailWorkflowsSourceReader(context, this.operator);
    context.inbox.registerSource(
      new MailTriageInboxSource(
        this.operator,
        this.threadOrdinals,
        sourceReader,
      ),
    );
    const classificationPrompt = await context.prompts.resolve(
      EMAIL_TRIAGE_CLASSIFICATION_PROMPT_TARGET,
      DEFAULT_EMAIL_TRIAGE_CLASSIFICATION_PROMPT,
    );
    const processor = new EmailTriageProcessor({
      repository: new EntityMailItemRepository(context.entityService),
      threadOrdinals: this.threadOrdinals,
      attempts: context.runtimeState.scoped({
        namespace: "email-workflows.classification-attempts",
        schema: z.number().int().min(1).max(3),
      }),
      classify: createMailClassifier(context.ai, classificationPrompt),
      logger: this.logger,
    });

    context.messaging.subscribe<InboundEmail>(EMAIL_INBOUND, async (message) =>
      processor.process(message.payload),
    );
  }

  protected override async onReady(
    context: ServicePluginContext,
  ): Promise<void> {
    if (!context.executionOnly) await this.getThreadOrdinals().initialize();
  }

  protected override async getTools(): Promise<Tool[]> {
    return [createEmailTriageListTool(this.getOperator())];
  }

  private getOperator(): MailTriageOperatorService {
    if (!this.operator) {
      throw new Error("Email triage operator service is not initialized");
    }
    return this.operator;
  }

  private getThreadOrdinals(): MailThreadOrdinalCoordinator {
    if (!this.threadOrdinals) {
      throw new Error("Email triage thread ordinals are not initialized");
    }
    return this.threadOrdinals;
  }
}
